import { z } from 'zod';
import { Timestamp } from 'firebase/firestore';

/**
 * Fonction utilitaire pour transformer les timestamps Firestore en Date
 */
const transformFirestoreTimestamp = (val: any): Date => {
  // Gérer null/undefined (cas de serverTimestamp() non encore résolu)
  if (val === null || val === undefined) {
    return new Date(); // Utiliser la date actuelle comme fallback
  }

  if (val instanceof Timestamp) {
    return val.toDate();
  }
  if (val instanceof Date) {
    return val;
  }
  if (typeof val === 'string') {
    return new Date(val);
  }
  if (typeof val === 'object' && val.seconds !== undefined) {
    // Timestamp Firestore sous forme d'objet
    return new Date(val.seconds * 1000);
  }

  // Fallback pour tout autre cas
  console.warn('Timestamp inattendu, utilisation de la date actuelle:', val);
  return new Date();
};

/**
 * Schéma Zod pour les timestamps Firestore
 */
const firestoreTimestampSchema = z.any().transform(transformFirestoreTimestamp);

/**
 * Enum des motifs de contact SAV
 */
export enum ChatReason {
  DEFECTIVE_PRODUCT = 'defective_product',
  DELAYED_DELIVERY = 'delayed_delivery',
  PRODUCT_QUESTION = 'product_question',
  REFUND_REQUEST = 'refund_request',
  OTHER = 'other',
}

/**
 * Enum des statuts de chat SAV
 */
export enum ChatStatus {
  OPEN = 'OPEN',
  IN_PROGRESS = 'IN_PROGRESS',
  CLOSED = 'CLOSED',
}

/**
 * Enum du type d'expéditeur de message
 */
export enum SenderType {
  CUSTOMER = 'CUSTOMER',
  ADMIN = 'ADMIN',
  SYSTEM = 'SYSTEM',
}

/**
 * Schéma Zod pour un message SAV
 */
export const SavMessageSchema = z.object({
  id: z.string(),
  senderType: z.nativeEnum(SenderType),
  senderId: z.string(),
  senderName: z.string(),
  message: z.string(),
  read: z.boolean().default(false),
  createdAt: firestoreTimestampSchema,
});

/**
 * Schéma Zod pour un chat SAV
 */
export const SavChatSchema = z.object({
  id: z.string(),
  orderId: z.string(),
  organizationId: z.string(),
  customerId: z.string(),
  customerName: z.string(),
  customerEmail: z.string(),
  orderReference: z.string(),
  status: z.nativeEnum(ChatStatus).default(ChatStatus.OPEN),
  reason: z.nativeEnum(ChatReason),
  reasonDetails: z.string().optional(),
  lastMessageAt: firestoreTimestampSchema,
  lastMessagePreview: z.string().default(''),
  unreadByAdmin: z.number().default(0),
  unreadByCustomer: z.number().default(0),
  createdAt: firestoreTimestampSchema,
  updatedAt: firestoreTimestampSchema,
});

/**
 * Interface TypeScript pour un message SAV
 */
export interface SavMessage {
  id: string;
  senderType: SenderType;
  senderId: string;
  senderName: string;
  message: string;
  read: boolean;
  createdAt: Date;
}

/**
 * Interface TypeScript pour un chat SAV
 */
export interface SavChat {
  id: string;
  orderId: string;
  organizationId: string;
  customerId: string;
  customerName: string;
  customerEmail: string;
  orderReference: string;
  status: ChatStatus;
  reason: ChatReason;
  reasonDetails?: string;
  lastMessageAt: Date;
  lastMessagePreview: string;
  unreadByAdmin: number;
  unreadByCustomer: number;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Interface pour créer un nouveau chat SAV
 */
export interface CreateSavChatRequest {
  orderId: string;
  organizationId: string;
  customerId: string;
  customerName: string;
  customerEmail: string;
  orderReference: string;
  reason: ChatReason;
  reasonDetails?: string;
  initialMessage: string;
}

/**
 * Interface pour créer un nouveau message
 */
export interface CreateSavMessageRequest {
  senderType: SenderType;
  senderId: string;
  senderName: string;
  message: string;
}

/**
 * Type pour les types d'expéditeurs
 */
export type ReaderType = 'admin' | 'customer';

/**
 * Labels des motifs de contact (pour l'affichage)
 */
export const ChatReasonLabels: Record<ChatReason, string> = {
  [ChatReason.DEFECTIVE_PRODUCT]: 'Produit défectueux',
  [ChatReason.DELAYED_DELIVERY]: 'Livraison retardée',
  [ChatReason.PRODUCT_QUESTION]: 'Question sur le produit',
  [ChatReason.REFUND_REQUEST]: 'Demande de remboursement',
  [ChatReason.OTHER]: 'Autre',
};

/**
 * Labels des statuts de chat (pour l'affichage)
 */
export const ChatStatusLabels: Record<ChatStatus, string> = {
  [ChatStatus.OPEN]: 'Ouvert',
  [ChatStatus.IN_PROGRESS]: 'En cours',
  [ChatStatus.CLOSED]: 'Résolu',
};

/**
 * Couleurs des statuts de chat
 */
export const ChatStatusColors: Record<ChatStatus, { bg: string; text: string }> = {
  [ChatStatus.OPEN]: { bg: 'bg-error-10', text: 'text-error-dark' },
  [ChatStatus.IN_PROGRESS]: { bg: 'bg-warning-10', text: 'text-warning-dark' },
  [ChatStatus.CLOSED]: { bg: 'bg-success-10', text: 'text-success-dark' },
};

